import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {
  "pageTitle": "Dropdown API",
  "title": "Dropdown",
  "props": ["DropdownProps", "DropdownContentProps", "DropdownHeaderProps", "DropdownMenuGroupProps", "DropdownMenuItemProps", "DropdownMenuNavItemProps", "DropdownButtonProps", "DropdownSplitButtonProps", "DropdownExpandableMenuItemProps", "DropdownExpandableMenuListItemProps", "DropdownExpandableMenuButtonProps", "DropdownExpandableMenuPanelProps"]
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const PageContent = makeShortcode("PageContent");
const LeadParagraph = makeShortcode("LeadParagraph");
const Link = makeShortcode("Link");
const DropdownProps = makeShortcode("DropdownProps");
const DropdownContentProps = makeShortcode("DropdownContentProps");
const DropdownHeaderProps = makeShortcode("DropdownHeaderProps");
const DropdownMenuGroupProps = makeShortcode("DropdownMenuGroupProps");
const DropdownMenuItemProps = makeShortcode("DropdownMenuItemProps");
const ButtonProps = makeShortcode("ButtonProps");
const IconButtonProps = makeShortcode("IconButtonProps");
const DropdownMenuNavItemProps = makeShortcode("DropdownMenuNavItemProps");
const DropdownExpandableMenuPanelProps = makeShortcode("DropdownExpandableMenuPanelProps");
const DropdownExpandableMenuButtonProps = makeShortcode("DropdownExpandableMenuButtonProps");
const DropdownExpandableMenuItemProps = makeShortcode("DropdownExpandableMenuItemProps");
const DropdownExpandableMenuListItemProps = makeShortcode("DropdownExpandableMenuListItemProps");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <PageContent componentName="dropdown" type="api" mdxType="PageContent">
      <LeadParagraph mdxType="LeadParagraph">
  Dropdowns (also referred to as Dropdown Menus) display a list of options that
  a user can select from within a contextual overlay.
      </LeadParagraph>
      <h2>{`Basic Usage`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`Dropdown`}</inlineCode>{` component should contain two children: the `}<inlineCode parentName="p">{`DropdownButton`}</inlineCode>{` component, and the `}<inlineCode parentName="p">{`DropdownContent`}</inlineCode>{` component.`}</p>
      <p>{`The `}<inlineCode parentName="p">{`DropdownButton`}</inlineCode>{` is a button that handles the opening and closing of the menu.`}</p>
      <p>{`The `}<inlineCode parentName="p">{`DropdownContent`}</inlineCode>{` component displays menu items (`}<inlineCode parentName="p">{`DropdownMenuItem`}</inlineCode>{`) when the button is clicked.`}</p>
      <p>{`When using multiple dropdowns, they should be wrapped in a `}<Link to="/api/buttongroup" mdxType="Link">{`ButtonGroup`}</Link>{`.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Dropdown,
  DropdownButton,
  DropdownContent,
  DropdownMenuItem,
} from 'react-magma-dom';

export function Example() {
  return (
    <Dropdown>
      <DropdownButton>Basic Dropdown</DropdownButton>
      <DropdownContent>
        <DropdownMenuItem>Menu item 1</DropdownMenuItem>
        <DropdownMenuItem>Menu item number two</DropdownMenuItem>
      </DropdownContent>
    </Dropdown>
  );
}
`}</code></pre>
      <h2>{`Split Button`}</h2>
      <p>{`Instead of using the `}<inlineCode parentName="p">{`DropdownButton`}</inlineCode>{`, a `}<inlineCode parentName="p">{`DropdownSplitButton`}</inlineCode>{` can be used instead. When using this, the larger part of the button will accept an `}<inlineCode parentName="p">{`onClick`}</inlineCode>{` event, and only the caret icon will open and close the menu.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Dropdown,
  DropdownContent,
  DropdownMenuItem,
  DropdownSplitButton,
} from 'react-magma-dom';

export function Example() {
  function handleSplitButtonClick() {
    alert('Button Clicked!');
  }

  return (
    <Dropdown>
      <DropdownSplitButton
        aria-label="Split Button"
        onClick={handleSplitButtonClick}
      >
        Split Button
      </DropdownSplitButton>
      <DropdownContent>
        <DropdownMenuItem>Menu item 1</DropdownMenuItem>
        <DropdownMenuItem>Menu item number two</DropdownMenuItem>
      </DropdownContent>
    </Dropdown>
  );
}
`}</code></pre>
      <h2>{`Drop Direction (deprecated)`}</h2>
      <p>{`Using the `}<inlineCode parentName="p">{`dropDirection`}</inlineCode>{` prop will change where the menu appears and the arrow direction. Options are `}<inlineCode parentName="p">{`down`}</inlineCode>{`, `}<inlineCode parentName="p">{`up`}</inlineCode>{`, `}<inlineCode parentName="p">{`left`}</inlineCode>{` and `}<inlineCode parentName="p">{`right`}</inlineCode>{` with `}<inlineCode parentName="p">{`down`}</inlineCode>{` being default.`}</p>
      <p><em parentName="p">{`Deprecation note:`}</em>{` Dropdowns now support viewport detection, so there is no need to use the `}<inlineCode parentName="p">{`dropDirection`}</inlineCode>{` prop. The dropdown will always open where there is enough space, and the arrow will always face down.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Dropdown,
  DropdownButton,
  DropdownContent,
  DropdownDropDirection,
  DropdownMenuItem,
  ButtonGroup,
} from 'react-magma-dom';

export function Example() {
  return (
    <ButtonGroup>
      <Dropdown dropDirection={DropdownDropDirection.left}>
        <DropdownButton>Drop Left</DropdownButton>
        <DropdownContent>
          <DropdownMenuItem>Menu item 1</DropdownMenuItem>
          <DropdownMenuItem>Menu item number two</DropdownMenuItem>
        </DropdownContent>
      </Dropdown>
      <Dropdown dropDirection={DropdownDropDirection.up}>
        <DropdownButton>Drop Up</DropdownButton>
        <DropdownContent>
          <DropdownMenuItem>Menu item 1</DropdownMenuItem>
          <DropdownMenuItem>Menu item number two</DropdownMenuItem>
        </DropdownContent>
      </Dropdown>
      <Dropdown dropDirection={DropdownDropDirection.right}>
        <DropdownButton>Drop Right</DropdownButton>
        <DropdownContent>
          <DropdownMenuItem>Menu item 1</DropdownMenuItem>
          <DropdownMenuItem>Menu item number two</DropdownMenuItem>
        </DropdownContent>
      </Dropdown>
    </ButtonGroup>
  );
}
`}</code></pre>
      <h2>{`Alignment (deprecated)`}</h2>
      <p>{`Using the `}<inlineCode parentName="p">{`alignment`}</inlineCode>{` prop will set the alignment of the content in relationship to the button.`}</p>
      <p>{`The default value is `}<inlineCode parentName="p">{`start`}</inlineCode>{`, meaning that a drop-down or a drop-up, will have the dropdown content aligned to the left side of the button,
and a drop-left or a drop-right will have the content aligned to the top of the button.`}</p>
      <p>{`The other option is `}<inlineCode parentName="p">{`end`}</inlineCode>{`, meaning that a drop-down or a drop-up, will have the dropdown content aligned to the right side of the button,
and a drop-left or a drop-right will have the content aligned to the bottom of the button.`}</p>
      <p><em parentName="p">{`Deprecation note:`}</em>{` Dropdowns now support viewport detection, so there is no need to use the `}<inlineCode parentName="p">{`alignment`}</inlineCode>{` prop.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Dropdown,
  DropdownAlignment,
  DropdownButton,
  DropdownContent,
  DropdownDropDirection,
  DropdownMenuItem,
  ButtonGroup,
} from 'react-magma-dom';

export function Example() {
  return (
    <>
      <ButtonGroup>
        <Dropdown
          alignment={DropdownAlignment.end}
          dropDirection={DropdownDropDirection.left}
        >
          <DropdownButton>Top-Aligned Drop Left</DropdownButton>
          <DropdownContent>
            <DropdownMenuItem>Menu item 1</DropdownMenuItem>
            <DropdownMenuItem>Menu item number two</DropdownMenuItem>
          </DropdownContent>
        </Dropdown>

        <Dropdown
          alignment={DropdownAlignment.end}
          dropDirection={DropdownDropDirection.right}
        >
          <DropdownButton>Top-Aligned Drop Right</DropdownButton>
          <DropdownContent>
            <DropdownMenuItem>Menu item 1</DropdownMenuItem>
            <DropdownMenuItem>Menu item number two</DropdownMenuItem>
          </DropdownContent>
        </Dropdown>
      </ButtonGroup>

      <br />

      <ButtonGroup>
        <Dropdown
          alignment={DropdownAlignment.end}
          dropDirection={DropdownDropDirection.down}
        >
          <DropdownButton>Right-Aligned Drop Down</DropdownButton>
          <DropdownContent>
            <DropdownMenuItem>Menu item 1</DropdownMenuItem>
            <DropdownMenuItem>Menu item number two</DropdownMenuItem>
          </DropdownContent>
        </Dropdown>

        <Dropdown
          alignment={DropdownAlignment.end}
          dropDirection={DropdownDropDirection.up}
        >
          <DropdownButton>Right-Aligned Drop Up</DropdownButton>
          <DropdownContent>
            <DropdownMenuItem>Menu item 1</DropdownMenuItem>
            <DropdownMenuItem>Menu item number two</DropdownMenuItem>
          </DropdownContent>
        </Dropdown>
      </ButtonGroup>
    </>
  );
}
`}</code></pre>
      <h2>{`Fixed Width`}</h2>
      <p>{`By default, the dropdown content will be as wide as the largest item. However, you may limit the width using the `}<inlineCode parentName="p">{`width`}</inlineCode>{` prop, in which case items will wrap if needed.
The `}<inlineCode parentName="p">{`width`}</inlineCode>{` prop can either be a string or a number. If a number is provided, then pixels will be used for the unit.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Dropdown,
  DropdownButton,
  DropdownContent,
  DropdownMenuItem,
} from 'react-magma-dom';

export function Example() {
  return (
    <Dropdown width="250px">
      <DropdownButton>Fixed Width</DropdownButton>
      <DropdownContent>
        <DropdownMenuItem>
          Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla tellus
          turpis, finibus et bibendum eget, rhoncus ut risus. Proin nec nisi ut
          eros aliquam interdum.
        </DropdownMenuItem>
        <DropdownMenuItem>
          Nunc egestas ullamcorper finibus. Phasellus non risus id mauris
          dignissim molestie in sit amet enim. In condimentum fringilla sodales.
        </DropdownMenuItem>
      </DropdownContent>
    </Dropdown>
  );
}
`}</code></pre>
      <h2>{`Max-Height`}</h2>
      <p>{`By default, the max-height of the dropdown content is 250px. That can be changed by using the `}<inlineCode parentName="p">{`maxHeight`}</inlineCode>{` prop.
The `}<inlineCode parentName="p">{`maxHeight`}</inlineCode>{` prop can either be a string or a number. If a number is provided, then pixels will be used for the unit.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Dropdown,
  DropdownButton,
  DropdownContent,
  DropdownMenuItem,
} from 'react-magma-dom';

export function Example() {
  return (
    <Dropdown maxHeight="150px">
      <DropdownButton>Max-Height</DropdownButton>
      <DropdownContent>
        <DropdownMenuItem>Menu item 1</DropdownMenuItem>
        <DropdownMenuItem>Menu item 2</DropdownMenuItem>
        <DropdownMenuItem>Menu item 3</DropdownMenuItem>
        <DropdownMenuItem>Menu item 4</DropdownMenuItem>
        <DropdownMenuItem>Menu item 5</DropdownMenuItem>
        <DropdownMenuItem>Menu item 6</DropdownMenuItem>
        <DropdownMenuItem>Menu item 7</DropdownMenuItem>
        <DropdownMenuItem>Menu item 8</DropdownMenuItem>
        <DropdownMenuItem>Menu item 9</DropdownMenuItem>
        <DropdownMenuItem>Menu item 10</DropdownMenuItem>
      </DropdownContent>
    </Dropdown>
  );
}
`}</code></pre>
      <h2>{`DropdownMenuItem`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`DropdownMenuItem`}</inlineCode>{` component can be used within the `}<inlineCode parentName="p">{`DropdownContent`}</inlineCode>{` to indicate individual menu items, which will render as buttons.
The items accept an `}<inlineCode parentName="p">{`onClick`}</inlineCode>{` event. The `}<inlineCode parentName="p">{`disabled`}</inlineCode>{` prop can be used on an item to disable that action. These items can be navigated by using the up and down arrow keys.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Dropdown,
  DropdownButton,
  DropdownContent,
  DropdownMenuItem,
} from 'react-magma-dom';

export function Example() {
  function handleMenuItem1Click() {
    alert('Item 1 clicked');
  }

  function handleMenuItem2Click() {
    alert('Item 2 clicked');
  }

  function handleMenuItem3Click() {
    alert('Item 3 clicked');
  }

  return (
    <Dropdown>
      <DropdownButton>Dropdown Menu Items</DropdownButton>
      <DropdownContent>
        <DropdownMenuItem onClick={handleMenuItem1Click}>
          Menu item 1
        </DropdownMenuItem>
        <DropdownMenuItem disabled onClick={handleMenuItem2Click}>
          Menu item 2
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleMenuItem3Click}>
          Menu item 3
        </DropdownMenuItem>
      </DropdownContent>
    </Dropdown>
  );
}
`}</code></pre>
      <h2>{`DropdownMenuNavItem`}</h2>
      <p>{`To render links within a dropdown, use the `}<inlineCode parentName="p">{`DropdownMenuNavItem`}</inlineCode>{` component. It accepts a `}<inlineCode parentName="p">{`to`}</inlineCode>{` prop, which is used as the link's href and accepts any other props that an anchor element would accept.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Dropdown,
  DropdownButton,
  DropdownContent,
  DropdownMenuNavItem,
} from 'react-magma-dom';

export function Example() {
  return (
    <Dropdown>
      <DropdownButton>Dropdown Menu Items</DropdownButton>
      <DropdownContent>
        <DropdownMenuNavItem to="http://www.google.com" target="_blank">
          Google
        </DropdownMenuNavItem>
        <DropdownMenuNavItem to="http://www.cengage.com" target="_blank">
          Cengage
        </DropdownMenuNavItem>
      </DropdownContent>
    </Dropdown>
  );
}
`}</code></pre>
      <h2>{`Expandable Menu Items`}</h2>
      <p>{`If a nested menu layout is needed within the `}<inlineCode parentName="p">{`Dropdown`}</inlineCode>{`, the `}<inlineCode parentName="p">{`DropdownExpandableMenuGroup`}</inlineCode>{` adds an expandable / collapsible menu structure.`}</p>
      <p>{`The components of `}<inlineCode parentName="p">{`DropdownExpandableMenuGroup`}</inlineCode>{`, `}<inlineCode parentName="p">{`DropdownExpandableMenuItem`}</inlineCode>{`, `}<inlineCode parentName="p">{`DropdownExpandableMenuButton`}</inlineCode>{`, `}<inlineCode parentName="p">{`DropdownExpandableMenuPanel`}</inlineCode>{`, and `}<inlineCode parentName="p">{`DropdownExpandableMenuListItem`}</inlineCode>{` necessitate the proper structure of this menu list.`}</p>
      <p>{`Please note that only one child panel is supported from a styling standpoint.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Dropdown,
  DropdownButton,
  DropdownContent,
  DropdownDivider,
  DropdownExpandableMenuGroup,
  DropdownExpandableMenuItem,
  DropdownExpandableMenuButton,
  DropdownExpandableMenuPanel,
  DropdownExpandableMenuListItem,
} from 'react-magma-dom';
import {
  RestaurantMenuIcon,
  LunchDiningIcon,
  LocalPizzaIcon,
} from 'react-magma-icons';

export function Example() {
  return (
    <Dropdown width={240}>
      <DropdownButton>Expandable Items Dropdown</DropdownButton>
      <DropdownContent>
        <DropdownExpandableMenuGroup>
          <DropdownExpandableMenuItem>
            <DropdownExpandableMenuButton icon={<RestaurantMenuIcon />}>
              Pasta
            </DropdownExpandableMenuButton>
            <DropdownExpandableMenuPanel>
              <DropdownExpandableMenuListItem>
                Fresh
              </DropdownExpandableMenuListItem>
              <DropdownExpandableMenuListItem>
                Processed
              </DropdownExpandableMenuListItem>
            </DropdownExpandableMenuPanel>
          </DropdownExpandableMenuItem>
          <DropdownExpandableMenuItem>
            <DropdownExpandableMenuButton icon={<LunchDiningIcon />}>
              Prosciutto
            </DropdownExpandableMenuButton>
            <DropdownExpandableMenuPanel>
              <DropdownExpandableMenuListItem>
                Domestic
              </DropdownExpandableMenuListItem>
              <DropdownExpandableMenuListItem>
                Speck
              </DropdownExpandableMenuListItem>
            </DropdownExpandableMenuPanel>
          </DropdownExpandableMenuItem>
        </DropdownExpandableMenuGroup>
        <DropdownDivider />
        <DropdownExpandableMenuListItem icon={<LocalPizzaIcon />}>
          Pizza
        </DropdownExpandableMenuListItem>
      </DropdownContent>
    </Dropdown>
  );
}
`}</code></pre>
      <h3>{`Expandable Menu with item auto expanded`}</h3>
      <p>{`By default, the `}<inlineCode parentName="p">{`isMulti`}</inlineCode>{` prop is true on `}<inlineCode parentName="p">{`DropdownExpandableMenuGroup`}</inlineCode>{`, which means that the expandable dropdown will allow multiple items to be expanded at once. If you wish to limit the number of items open at once, you can set the `}<inlineCode parentName="p">{`isMulti`}</inlineCode>{` prop to false.`}</p>
      <p>{`The `}<inlineCode parentName="p">{`defaultIndex`}</inlineCode>{` prop on `}<inlineCode parentName="p">{`DropdownExpandableMenuGroup`}</inlineCode>{` uses an array of zero based indices to enable expandable groups to be automatically opened.`}</p>
      <p>{`When the `}<inlineCode parentName="p">{`isMulti`}</inlineCode>{` prop is false, the `}<inlineCode parentName="p">{`defaultIndex`}</inlineCode>{` prop will take a single number, instead of an array.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Dropdown,
  DropdownButton,
  DropdownContent,
  DropdownDivider,
  DropdownExpandableMenuGroup,
  DropdownExpandableMenuItem,
  DropdownExpandableMenuButton,
  DropdownExpandableMenuPanel,
  DropdownExpandableMenuListItem,
} from 'react-magma-dom';
import {
  RestaurantMenuIcon,
  LunchDiningIcon,
  LocalPizzaIcon,
} from 'react-magma-icons';

export function Example() {
  return (
    <Dropdown width={240}>
      <DropdownButton>Expandable Items Dropdown</DropdownButton>
      <DropdownContent>
        <DropdownExpandableMenuGroup isMulti defaultIndex={[0]}>
          <DropdownExpandableMenuItem>
            <DropdownExpandableMenuButton icon={<RestaurantMenuIcon />}>
              Pasta
            </DropdownExpandableMenuButton>
            <DropdownExpandableMenuPanel>
              <DropdownExpandableMenuListItem>
                Fresh
              </DropdownExpandableMenuListItem>
              <DropdownExpandableMenuListItem>
                Processed
              </DropdownExpandableMenuListItem>
            </DropdownExpandableMenuPanel>
          </DropdownExpandableMenuItem>
          <DropdownExpandableMenuItem>
            <DropdownExpandableMenuButton icon={<LunchDiningIcon />}>
              Prosciutto
            </DropdownExpandableMenuButton>
            <DropdownExpandableMenuPanel>
              <DropdownExpandableMenuListItem>
                Domestic
              </DropdownExpandableMenuListItem>
              <DropdownExpandableMenuListItem>
                Speck
              </DropdownExpandableMenuListItem>
            </DropdownExpandableMenuPanel>
          </DropdownExpandableMenuItem>
        </DropdownExpandableMenuGroup>
        <DropdownDivider />
        <DropdownExpandableMenuListItem icon={<LocalPizzaIcon />}>
          Pizza
        </DropdownExpandableMenuListItem>
      </DropdownContent>
    </Dropdown>
  );
}
`}</code></pre>
      <h3>{`Text only Expandable Menu Item`}</h3>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Dropdown,
  DropdownButton,
  DropdownContent,
  DropdownDivider,
  DropdownExpandableMenuGroup,
  DropdownExpandableMenuItem,
  DropdownExpandableMenuButton,
  DropdownExpandableMenuPanel,
  DropdownExpandableMenuListItem,
} from 'react-magma-dom';

export function Example() {
  return (
    <Dropdown width={240}>
      <DropdownButton>Expandable Items Dropdown</DropdownButton>
      <DropdownContent>
        <DropdownExpandableMenuGroup>
          <DropdownExpandableMenuItem>
            <DropdownExpandableMenuButton>Pasta</DropdownExpandableMenuButton>
            <DropdownExpandableMenuPanel>
              <DropdownExpandableMenuListItem>
                Fresh
              </DropdownExpandableMenuListItem>
              <DropdownExpandableMenuListItem>
                Processed
              </DropdownExpandableMenuListItem>
            </DropdownExpandableMenuPanel>
          </DropdownExpandableMenuItem>
          <DropdownExpandableMenuItem>
            <DropdownExpandableMenuButton>
              Prosciutto
            </DropdownExpandableMenuButton>
            <DropdownExpandableMenuPanel>
              <DropdownExpandableMenuListItem>
                Domestic
              </DropdownExpandableMenuListItem>
              <DropdownExpandableMenuListItem>
                Speck
              </DropdownExpandableMenuListItem>
            </DropdownExpandableMenuPanel>
          </DropdownExpandableMenuItem>
        </DropdownExpandableMenuGroup>
        <DropdownDivider />
        <DropdownExpandableMenuListItem>Pizza</DropdownExpandableMenuListItem>
      </DropdownContent>
    </Dropdown>
  );
}
`}</code></pre>
      <h2>{`Custom DropdownMenuItem Wrappers`}</h2>
      <p><inlineCode parentName="p">{`DropdownContent`}</inlineCode>{` expects its children to be of type `}<inlineCode parentName="p">{`DropdownMenuItem`}</inlineCode>{`, be an element in which its lowest child is of type `}<inlineCode parentName="p">{`DropdownMenuItem`}</inlineCode>{`, or a custom component
that returns a `}<inlineCode parentName="p">{`DropdownMenuItem`}</inlineCode>{`.`}</p>
      <p>{`For a custom component you'll receive a `}<inlineCode parentName="p">{`dropdownMenuItemProps`}</inlineCode>{` prop that you must spread in to the `}<inlineCode parentName="p">{`DropdownMenuItem`}</inlineCode>{` component in your renderer.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Dropdown,
  DropdownButton,
  DropdownContent,
  DropdownMenuItem,
} from 'react-magma-dom';

export function Example() {
  const OptionalDropdownMenuItem = ({ toggle, ...dropdownMenuItemProps }) => {
    return toggle ? (
      <DropdownMenuItem {...dropdownMenuItemProps}>
        Hello There
      </DropdownMenuItem>
    ) : null;
  };

  function handleMenuItem1Click() {
    alert('Item 1 clicked');
  }

  function handleMenuItem2Click() {
    alert('Item 2 clicked');
  }

  function handleMenuItem3Click() {
    alert('Item 3 clicked');
  }

  return (
    <Dropdown>
      <DropdownButton>Dropdown Menu Items</DropdownButton>
      <DropdownContent>
        <DropdownMenuItem onClick={handleMenuItem1Click}>
          Menu item 1
        </DropdownMenuItem>
        <OptionalDropdownMenuItem toggle onClick={handleMenuItem2Click}>
          Menu item 2
        </OptionalDropdownMenuItem>
        <div>
          <DropdownMenuItem onClick={handleMenuItem3Click}>
            Menu item 3
          </DropdownMenuItem>
        </div>
      </DropdownContent>
    </Dropdown>
  );
}
`}</code></pre>
      <h3>{`Leading Icons`}</h3>
      <p>{`The `}<inlineCode parentName="p">{`icon`}</inlineCode>{` prop can be used to specify an icon for the `}<inlineCode parentName="p">{`DropdownMenuItem`}</inlineCode>{` elements.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Dropdown,
  DropdownButton,
  DropdownContent,
  DropdownMenuItem,
} from 'react-magma-dom';
import { AsteriskIcon, NotificationsIcon } from 'react-magma-icons';

export function Example() {
  return (
    <Dropdown>
      <DropdownButton>Leading Icons</DropdownButton>
      <DropdownContent>
        <DropdownMenuItem icon={<AsteriskIcon />}>Menu item 1</DropdownMenuItem>
        <DropdownMenuItem icon={<NotificationsIcon />}>
          Menu item 2
        </DropdownMenuItem>
      </DropdownContent>
    </Dropdown>
  );
}
`}</code></pre>
      <h2>{`Dropdown without Menu Items`}</h2>
      <p>{`The Dropdown component is technically capable of displaying content other than menus. But for all non-menu content, please use the `}<Link to="/api/popover" mdxType="Link">{`Popover component`}</Link>{`. The Popover has been specifically designed for the usability and accessibility needs of such content.`}</p>
      <h2>{`Custom Button`}</h2>
      <p>{`Both the `}<inlineCode parentName="p">{`DropdownButton`}</inlineCode>{` and `}<inlineCode parentName="p">{`DropdownSplitButton`}</inlineCode>{`can be customized by using any of the props that the `}<Link to="/api/button" mdxType="Link">{`Button`}</Link>{` component accepts. If no`}<inlineCode parentName="p">{`icon`}</inlineCode>{`property is provided to the`}<inlineCode parentName="p">{`DropdownButton`}</inlineCode>{`, the caret icon will appear.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  ButtonColor,
  ButtonSize,
  ButtonGroup,
  ButtonVariant,
  Dropdown,
  DropdownButton,
  DropdownContent,
  DropdownMenuItem,
  DropdownSplitButton,
} from 'react-magma-dom';

export function Example() {
  return (
    <ButtonGroup>
      <Dropdown>
        <DropdownButton size={ButtonSize.small} color={ButtonColor.marketing}>
          Extra Props Example 1
        </DropdownButton>
        <DropdownContent>
          <DropdownMenuItem>Menu item 1</DropdownMenuItem>
          <DropdownMenuItem>Menu item number two</DropdownMenuItem>
        </DropdownContent>
      </Dropdown>
      <Dropdown>
        <DropdownSplitButton
          size={ButtonSize.large}
          variant={ButtonVariant.solid}
          color={ButtonColor.danger}
          aria-label={'Custom Button Example'}
        >
          Extra Props Split
        </DropdownSplitButton>
        <DropdownContent>
          <DropdownMenuItem>Menu item 1</DropdownMenuItem>
          <DropdownMenuItem>Menu item number two</DropdownMenuItem>
        </DropdownContent>
      </Dropdown>
    </ButtonGroup>
  );
}
`}</code></pre>
      <h2>{`Custom Icon Button`}</h2>
      <p>{`Similarly, the `}<inlineCode parentName="p">{`DropdownButton`}</inlineCode>{` can be customized by using any of the props that the `}<Link to="/api/icon-button" mdxType="Link">{`Icon Button`}</Link>{` component accepts, to create a button with an icon.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  ButtonColor,
  ButtonIconPosition,
  ButtonSize,
  ButtonVariant,
  ButtonGroup,
  Dropdown,
  DropdownButton,
  DropdownContent,
  DropdownMenuItem,
} from 'react-magma-dom';
import { SettingsIcon, MenuIcon } from 'react-magma-icons';

export function Example() {
  return (
    <>
      <ButtonGroup>
        <Dropdown>
          <DropdownButton
            aria-label="Extra icon example"
            color={ButtonColor.secondary}
            size={ButtonSize.large}
            variant={ButtonVariant.link}
            icon={<SettingsIcon />}
          />
          <DropdownContent>
            <DropdownMenuItem>Menu item 1</DropdownMenuItem>
            <DropdownMenuItem>Menu item number two</DropdownMenuItem>
          </DropdownContent>
        </Dropdown>
        <Dropdown>
          <DropdownButton
            aria-label="Extra icon example"
            size={ButtonSize.large}
            icon={<MenuIcon />}
          />
          <DropdownContent>
            <DropdownMenuItem>Menu item 1</DropdownMenuItem>
            <DropdownMenuItem>Menu item number two</DropdownMenuItem>
          </DropdownContent>
        </Dropdown>
        <Dropdown>
          <DropdownButton
            variant={ButtonVariant.solid}
            size={ButtonSize.small}
            iconPosition={ButtonIconPosition.right}
            icon={<SettingsIcon />}
          >
            Icon Right with Text
          </DropdownButton>
          <DropdownContent>
            <DropdownMenuItem>Menu item 1</DropdownMenuItem>
            <DropdownMenuItem>Menu item number two</DropdownMenuItem>
          </DropdownContent>
        </Dropdown>
        <Dropdown>
          <DropdownButton
            variant={ButtonVariant.solid}
            size={ButtonSize.small}
            iconPosition={ButtonIconPosition.left}
            icon={<SettingsIcon />}
          >
            Icon Left with Text
          </DropdownButton>
          <DropdownContent>
            <DropdownMenuItem>Menu item 1</DropdownMenuItem>
            <DropdownMenuItem>Menu item number two</DropdownMenuItem>
          </DropdownContent>
        </Dropdown>
      </ButtonGroup>
    </>
  );
}
`}</code></pre>
      <h2>{`Breaking a Menu Into Groups`}</h2>
      <p>{`A `}<inlineCode parentName="p">{`DropdownMenu`}</inlineCode>{` can be broken into multiple groups, by using the `}<inlineCode parentName="p">{`DropdownMenuGroup`}</inlineCode>{` and `}<inlineCode parentName="p">{`DropdownDivider`}</inlineCode>{` components.`}</p>
      <p>{`The `}<inlineCode parentName="p">{`DropdownMenuGroup`}</inlineCode>{` component accepts a `}<inlineCode parentName="p">{`header`}</inlineCode>{` prop to be used to describe the group. The menu group `}<inlineCode parentName="p">{`div`}</inlineCode>{`
will be given a `}<inlineCode parentName="p">{`role`}</inlineCode>{` of `}<inlineCode parentName="p">{`group`}</inlineCode>{` and an `}<inlineCode parentName="p">{`aria-labelledbyId`}</inlineCode>{` that corresponds with the header made internally.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Dropdown,
  DropdownButton,
  DropdownContent,
  DropdownDivider,
  DropdownMenuGroup,
  DropdownMenuItem,
} from 'react-magma-dom';

export function Example() {
  return (
    <Dropdown>
      <DropdownButton>Divider and headings</DropdownButton>
      <DropdownContent>
        <DropdownMenuGroup header="Section title A">
          <DropdownMenuItem>Menu item 1</DropdownMenuItem>
          <DropdownMenuItem>Menu item 2</DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownDivider />
        <DropdownMenuGroup header="Section title B">
          <DropdownMenuItem>Menu item 3</DropdownMenuItem>
          <DropdownMenuItem>Menu item 4</DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownDivider />
        <DropdownMenuGroup>
          <DropdownMenuItem>Log out</DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownContent>
    </Dropdown>
  );
}
`}</code></pre>
      <h2>{`Active Item Index`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`activeIndex`}</inlineCode>{` prop can be used to set an item within the dropdown as active.
When another item is selected, that item will become active and the menu will remain open, rather than closing as usual.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Dropdown,
  DropdownButton,
  DropdownContent,
  DropdownMenuItem,
} from 'react-magma-dom';

export function Example() {
  const [activeValue, setActiveValue] = React.useState<string>('two');

  function changeActiveValue(value: string) {
    setActiveValue(value);
  }

  return (
    <>
      <p>
        <strong>Active Value: </strong> <span>{activeValue}</span>
      </p>
      <Dropdown activeIndex={1}>
        <DropdownButton>Selected Item</DropdownButton>
        <DropdownContent>
          <DropdownMenuItem onClick={changeActiveValue} value="one">
            Dropdown Item 1
          </DropdownMenuItem>
          <DropdownMenuItem onClick={changeActiveValue} value="two">
            Dropdown Item 2
          </DropdownMenuItem>
          <DropdownMenuItem onClick={changeActiveValue} value="three">
            Dropdown Item 3
          </DropdownMenuItem>
        </DropdownContent>
      </Dropdown>
    </>
  );
}
`}</code></pre>
      <h2>{`Handle Focus on Close`}</h2>
      <p>{`By default the consumer is expected to handle the focusing of elements when items are clicked. If there is no new element to focus on item click
you should focus the trigger button on closing of the dropdown. For this you can use a `}<inlineCode parentName="p">{`ref`}</inlineCode>{` passed to the `}<inlineCode parentName="p">{`DropdownButton`}</inlineCode>{` or `}<inlineCode parentName="p">{`DropdownSplitButton`}</inlineCode>{` and focus that ref in
the `}<inlineCode parentName="p">{`onClose`}</inlineCode>{` function passed to the `}<inlineCode parentName="p">{`Dropdown`}</inlineCode>{` component.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Dropdown,
  DropdownButton,
  DropdownContent,
  DropdownMenuItem,
} from 'react-magma-dom';

export function Example() {
  const buttonRef = React.useRef<HTMLButtonElement>();

  function handleClose(event: React.SyntheticEvent) {
    buttonRef.current.focus();
  }

  return (
    <Dropdown onClose={handleClose}>
      <DropdownButton ref={buttonRef}>Basic Dropdown</DropdownButton>
      <DropdownContent>
        <DropdownMenuItem>Menu item 1</DropdownMenuItem>
        <DropdownMenuItem>Menu item number two</DropdownMenuItem>
      </DropdownContent>
    </Dropdown>
  );
}
`}</code></pre>
      <h2>{`Inverse`}</h2>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Dropdown,
  DropdownButton,
  DropdownContent,
  DropdownMenuItem,
  Card,
  CardBody,
} from 'react-magma-dom';

export function Example() {
  return (
    <Card isInverse>
      <CardBody>
        <Dropdown isInverse>
          <DropdownButton>Basic Dropdown</DropdownButton>
          <DropdownContent>
            <DropdownMenuItem>Menu item one</DropdownMenuItem>
            <DropdownMenuItem>Menu item two</DropdownMenuItem>
          </DropdownContent>
        </Dropdown>
      </CardBody>
    </Card>
  );
}
`}</code></pre>
      <h2>{`Dropdown Props`}</h2>
      <p><strong parentName="p">{`Any other props supplied will be provided to the wrapping `}<inlineCode parentName="strong">{`div`}</inlineCode>{` element.`}</strong></p>
      <DropdownProps mdxType="DropdownProps" />
      <h2>{`Dropdown Content Props`}</h2>
      <p><strong parentName="p">{`Props supplied will be provided to the wrapping `}<inlineCode parentName="strong">{`div`}</inlineCode>{` element.`}</strong></p>
      <p>{`Examples may include an id, a title, event handlers or various aria attributes. It is unlikely that it will be necessary to provide additional props.`}</p>
      <DropdownContentProps mdxType="DropdownContentProps" />
      <h2>{`Dropdown Header Props`}</h2>
      <p><strong parentName="p">{`Any other props supplied will be provided to the wrapping `}<inlineCode parentName="strong">{`div`}</inlineCode>{` element.`}</strong></p>
      <DropdownHeaderProps mdxType="DropdownHeaderProps" />
      <h2>{`Dropdown Menu Group`}</h2>
      <p><strong parentName="p">{`Any other props supplied will be provided to the wrapping `}<inlineCode parentName="strong">{`div`}</inlineCode>{` element.`}</strong></p>
      <DropdownMenuGroupProps mdxType="DropdownMenuGroupProps" />
      <h2>{`Dropdown Menu Item`}</h2>
      <p><strong parentName="p">{`Any other props supplied will be provided to the wrapping `}<inlineCode parentName="strong">{`li`}</inlineCode>{` element.`}</strong></p>
      <DropdownMenuItemProps mdxType="DropdownMenuItemProps" />
      <h2>{`Dropdown Button Props`}</h2>
      <p><strong parentName="p">{`DropdownButton and the DropdownSplitButton accept the same props as the `}<Link to="/api/button" mdxType="Link">{`Button`}</Link>{` component.`}</strong></p>
      <ButtonProps mdxType="ButtonProps" />
      <p>{`The DropdownButton can also accept an `}<inlineCode parentName="p">{`icon`}</inlineCode>{` property, as in the `}<Link to="/api/icon-button" mdxType="Link">{`Icon Button`}</Link>{` component.`}</p>
      <IconButtonProps mdxType="IconButtonProps" />
      <h2>{`Dropdown Menu Nav Item`}</h2>
      <p><strong parentName="p">{`Any other props supplied will be provided to the wrapping `}<inlineCode parentName="strong">{`a`}</inlineCode>{` element.`}</strong></p>
      <DropdownMenuNavItemProps mdxType="DropdownMenuNavItemProps" />
      <h2>{`Dropdown Expandable Menu Panel Props`}</h2>
      <p><strong parentName="p">{`Any other props supplied will be provided to the wrapping `}<inlineCode parentName="strong">{`div`}</inlineCode>{` element.`}</strong></p>
      <DropdownExpandableMenuPanelProps mdxType="DropdownExpandableMenuPanelProps" />
      <h2>{`Dropdown Expandable Menu Button Props`}</h2>
      <p><strong parentName="p">{`Any other props supplied will be provided to the wrapping `}<inlineCode parentName="strong">{`div`}</inlineCode>{` element.`}</strong></p>
      <DropdownExpandableMenuButtonProps mdxType="DropdownExpandableMenuButtonProps" />
      <h2>{`Dropdown Expandable Menu Item Props`}</h2>
      <p><strong parentName="p">{`Any other props supplied will be provided to the wrapping `}<inlineCode parentName="strong">{`div`}</inlineCode>{` element.`}</strong></p>
      <DropdownExpandableMenuItemProps mdxType="DropdownExpandableMenuItemProps" />
      <h2>{`Dropdown Expandable Menu List Item Props`}</h2>
      <p><strong parentName="p">{`Any other props supplied will be provided to the wrapping `}<inlineCode parentName="strong">{`div`}</inlineCode>{` element.`}</strong></p>
      <DropdownExpandableMenuListItemProps mdxType="DropdownExpandableMenuListItemProps" />
    </PageContent>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      